export type PdfMetaCategory = 'identity' | 'datetime' | 'software';

export type PdfMetaField = {
  key: PdfMetaKey;
  label: string;
  category: PdfMetaCategory;
  display: string;
};

export type PdfMetaKey =
  | 'title'
  | 'author'
  | 'subject'
  | 'keywords'
  | 'creator'
  | 'producer'
  | 'creationDate'
  | 'modificationDate';

export type PdfMetaResult = {
  fields: PdfMetaField[];
};

const LABEL: Record<PdfMetaKey, string> = {
  title: 'title',
  author: 'author',
  subject: 'subject',
  keywords: 'keywords',
  creator: 'creator',
  producer: 'producer',
  creationDate: 'creation date',
  modificationDate: 'modification date',
};

const CATEGORY: Record<PdfMetaKey, PdfMetaCategory> = {
  title: 'identity',
  author: 'identity',
  subject: 'identity',
  keywords: 'identity',
  creator: 'software',
  producer: 'software',
  creationDate: 'datetime',
  modificationDate: 'datetime',
};

type PdfDocumentLike = {
  getTitle: () => string | undefined;
  getAuthor: () => string | undefined;
  getSubject: () => string | undefined;
  getKeywords: () => string | undefined;
  getCreator: () => string | undefined;
  getProducer: () => string | undefined;
  getCreationDate: () => Date | undefined;
  getModificationDate: () => Date | undefined;
};

const stringify = (value: string | Date | undefined): string => {
  if (value === undefined) return '';
  if (value instanceof Date) return value.toISOString();
  return value;
};

const READERS: Record<PdfMetaKey, (d: PdfDocumentLike) => string | Date | undefined> = {
  title: (d) => d.getTitle(),
  author: (d) => d.getAuthor(),
  subject: (d) => d.getSubject(),
  keywords: (d) => d.getKeywords(),
  creator: (d) => d.getCreator(),
  producer: (d) => d.getProducer(),
  creationDate: (d) => d.getCreationDate(),
  modificationDate: (d) => d.getModificationDate(),
};

/**
 * Extract metadata fields from a parsed pdf-lib PDFDocument.
 * Empty/undefined fields are skipped — no point asking the user about
 * something the file doesn't expose.
 */
export const detectPdfMetadata = (pdfDoc: PdfDocumentLike): PdfMetaResult => {
  const fields: PdfMetaField[] = [];
  for (const key of Object.keys(READERS) as PdfMetaKey[]) {
    const value = READERS[key](pdfDoc);
    const display = stringify(value);
    if (!display) continue;
    fields.push({
      key,
      label: LABEL[key],
      category: CATEGORY[key],
      display,
    });
  }
  return { fields };
};
